import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { PageTabs } from '@/components/PageTabs';
import { StatusBadge } from '@/components/StatusBadge';
import { AppHeaderActions } from '@/components/apps/AppHeaderActions';
import { ConfigTab } from '@/components/apps/ConfigTab';
import { DeploymentTab } from '@/components/apps/DeploymentTab';
import { LogsTab } from '@/components/apps/LogsTab';
import { OverviewTab } from '@/components/apps/OverviewTab';
import { StatsTab } from '@/components/apps/StatsTab';
import { useAppActions } from '@/components/apps/useAppActions';
import { appUrls } from '@/lib/appUrls';
import { useAppDetail } from '@/lib/hooks/useAppDetail';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'logs', label: 'Logs' },
  { id: 'stats', label: 'Stats' },
  { id: 'config', label: 'Config' },
  { id: 'deployments', label: 'Deployments' },
];

const backLink = (
  <Link to="/apps" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
    Back to apps
  </Link>
);

export function AppDetailPage() {
  const { appId } = useParams<{ appId: string }>();
  // Keyed so that navigating between apps remounts the polling hook and every tab's state.
  return <AppDetail key={appId} rawId={appId} />;
}

function AppDetail({ rawId }: { rawId: string | undefined }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const id = rawId !== undefined && /^\d+$/.test(rawId) && Number(rawId) > 0 ? Number(rawId) : null;
  const { app, loading, error, notFound, refetch, seedDomainSwitch, domains, domainSwitch, switchUpdatedAt } = useAppDetail(id);
  const actions = useAppActions({
    onChanged: refetch,
    onDeleted: () => navigate('/apps'),
    domains,
    onDomainSwitchStarted: seedDomainSwitch,
  });

  const tabParam = params.get('tab');
  const tab = TABS.some((t) => t.id === tabParam) ? (tabParam as string) : 'overview';
  const selectTab = (next: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'overview') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true },
    );

  // Not found wins even when stale data is loaded (app deleted elsewhere while the page was open).
  if (id === null || notFound) {
    return <EmptyState title="App not found" description="This app does not exist or was deleted." action={backLink} />;
  }

  if (!app) {
    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      );
    }
    return (
      <EmptyState
        title="Could not load app"
        description={error ?? undefined}
        action={
          <Button variant="outline" size="sm" onClick={refetch}>
            Retry
          </Button>
        }
      />
    );
  }

  const main = appUrls(app)[0];
  const description = (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {main && (
        <a
          href={main.href}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all rounded-sm text-primary underline-offset-4 hover:underline outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {main.host}
        </a>
      )}
      <StatusBadge status={app.status} />
      <span>ID {app.id}</span>
    </span>
  );

  return (
    <>
      {/* main is the scroller and the wrapper pads it by p-4/md:p-8; the negative margin pulls this block
          flush to the scroller's top/left/right so sticky top-0 leaves no gap above it. */}
      <div className="-mx-4 -mt-4 border-b bg-background px-4 pb-0 pt-4 md:sticky md:top-0 md:z-20 md:-mx-8 md:-mt-8 md:bg-background/95 md:px-8 md:pt-8 md:backdrop-blur">
        <PageHeader
          title={app.display_name || app.name}
          description={description}
          actions={
            <AppHeaderActions
              app={app}
              onAction={actions.request}
              isBusy={actions.isBusy}
              onRefresh={refetch}
              domains={domains}
              domainSwitch={domainSwitch}
            />
          }
        >
          <PageTabs tabs={TABS} active={tab} onSelect={selectTab} label="App sections" className="border-b-0" />
        </PageHeader>
      </div>

      {error !== null && (
        <div role="status" className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning">
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}

      {tab === 'overview' && (
        <OverviewTab key={app.id} app={app} domainSwitch={domainSwitch} now={switchUpdatedAt ?? undefined} />
      )}
      {tab === 'logs' && <LogsTab key={app.id} appId={app.id} appStatus={app.status} />}
      {tab === 'stats' && <StatsTab key={app.id} appId={app.id} appStatus={app.status} />}
      {tab === 'config' && <ConfigTab key={app.id} app={app} />}
      {tab === 'deployments' && <DeploymentTab key={app.id} app={app} />}
      {actions.dialogs}
    </>
  );
}
